import { Badge } from "@bcc/components/ui/badge";
import { AlertTriangle } from "lucide-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { ConnectedVariantsInfo, GetRemoteFormVariantData, GrindOption, ProductSyncOption } from "./product-sync-modal.types";
import { setVariantRowPlaceholderTestId, setProductSyncModalTestId } from "./product-sync-modal.utils";
import { VariantRowSingle } from "./variant-row-single";

export interface VariantRowPlaceholderProps {
  grinds: ProductSyncOption[];
  values: ProductSyncOption[];
  getRemoteFormVariant: (targetVariantId: string) => GetRemoteFormVariantData | undefined;
  bagSize: string;
  onChange: (targetVariantId: string) => (grinds: string[]) => void;
  connectedVariantsInfo: ConnectedVariantsInfo[];
  isGrindMappingDisabled: boolean;
  selectedGrindsByBagSize: Set<string>;
  localVariantId: string;
}

export function VariantRowPlaceholder({
  grinds,
  values,
  onChange,
  getRemoteFormVariant,
  connectedVariantsInfo,
  isGrindMappingDisabled,
  bagSize,
  selectedGrindsByBagSize,
  localVariantId,
}: VariantRowPlaceholderProps) {
  const { t } = useTranslation();

  const grindOptions = useMemo(() => {
    if (!Array.isArray(grinds)) {
      return [];
    }
    return [...grinds].map((option) => {
      const isOptionDisabled = selectedGrindsByBagSize.has(option.value);
      return {
        label: option.label,
        value: option.value,
        disabled: isOptionDisabled || isGrindMappingDisabled,
      } satisfies GrindOption;
    });
  }, [grinds, isGrindMappingDisabled, selectedGrindsByBagSize]);

  const getGrindOptions = (variantId: string): GrindOption[] => {
    const mappedGrinds = connectedVariantsInfo.find((variant) => variant.id === variantId)?.grinds ?? [];
    return grindOptions.map((option) => {
      return { ...option, checked: mappedGrinds.includes(option.value) };
    });
  };

  return (
    <div data-testid={setVariantRowPlaceholderTestId(localVariantId, bagSize)}>
      {values.length === 0 && (
        <div className="mt-3">
          <Badge
            variant="secondary"
            className="bg-yellow-100 text-yellow-900 hover:bg-yellow-100"
            testId={setProductSyncModalTestId("variant-select-error")}
          >
            <AlertTriangle className="mr-1 h-3 w-3" />
            {t("components.ProductSyncModal.variantSelectError", {
              defaultValue: "Please select at least one remote variant to map.",
            })}
          </Badge>
        </div>
      )}
      {values.length > 0 && (
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <span className="text-sm font-semibold">
            {t("components.ProductSyncModal.grindMapping", {
              defaultValue: "Grind Mapping",
            })}
          </span>
          <Badge
            variant="secondary"
            className="bg-blue-100 text-blue-900 hover:bg-blue-100"
            testId={setProductSyncModalTestId("variant-mapping-info")}
          >
            <AlertTriangle className="mr-1 h-3 w-3" />
            {t("components.ProductSyncModal.variantMappingInfo", {
              defaultValue: "Select grinds for each mapped variant. Duplicate grinds are prevented per bag size.",
            })}
          </Badge>
        </div>
      )}
      {values.length > 0 && (
        <div className="mt-3 rounded-md bg-muted/40 p-3">
          <div className="mb-2 grid gap-3 xl:grid-cols-[minmax(180px,240px)_1fr]">
            <strong className="text-sm">
              {t("components.ProductSyncModal.variantHeader", {
                defaultValue: "Variant",
              })}
            </strong>
            <strong className="text-sm">
              {t("components.ProductSyncModal.grindsHeader", {
                defaultValue: "Grinds",
              })}
            </strong>
          </div>
          <div className="space-y-2">
            {values.map((value) => {
              return (
                <VariantRowSingle
                  key={value.value}
                  remoteVariant={getRemoteFormVariant(value.value)?.remoteFormVariant}
                  remoteProductTitle={getRemoteFormVariant(value.value)?.remoteProductTitle}
                  value={value}
                  handleChange={onChange}
                  grindsOptions={getGrindOptions(value.value)}
                  isGrindMappingDisabled={isGrindMappingDisabled}
                />
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
